import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Loader2 } from "lucide-react";

const LOGO_URL = "/assets/logo-circle.svg";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        if (!formData.name) {
          toast.error("El nombre es requerido");
          setLoading(false);
          return;
        }
        await register(formData.email, formData.password, formData.name);
        toast.success("Cuenta creada exitosamente");
      } else {
        await login(formData.email, formData.password);
        toast.success("Bienvenido de vuelta");
      }
      navigate("/admin");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-circulo-black flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img
            src={LOGO_URL}
            alt="Circle"
            className="h-16 w-16 rounded-full object-cover invert mx-auto mb-4"
          />
          <h1 className="text-3xl font-serif mb-2">
            {isRegister ? "Crear cuenta" : "Acceso Admin"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isRegister 
              ? "Crea una cuenta de administrador" 
              : "Ingresa tus credenciales para continuar"}
          </p>
        </div>

        <div className="p-8 bg-circulo-paper border border-white/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-muted-foreground">
                  Nombre
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand"
                  placeholder="Tu nombre"
                  data-testid="admin-name"
                />
              </div>
            )}

            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand"
                placeholder="admin@elcirculo.mx"
                data-testid="admin-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="mt-2 bg-transparent border-white/20 focus:border-circulo-sand"
                placeholder="••••••••"
                data-testid="admin-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center gap-2"
              data-testid="admin-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {isRegister ? "Creando cuenta..." : "Iniciando sesión..."}
                </>
              ) : (
                isRegister ? "Crear cuenta" : "Iniciar sesión"
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-muted-foreground hover:text-white transition-colors"
            >
              {isRegister 
                ? "¿Ya tienes cuenta? Inicia sesión" 
                : "¿No tienes cuenta? Regístrate"}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <a
            href="/"
            className="text-sm text-muted-foreground hover:text-circulo-sand transition-colors"
          >
            Volver al sitio
          </a>
        </div>
      </motion.div>
    </div>
  );
}
